import Auth from '@aws-amplify/auth';

export default class S3Helper {
  /**
   * Pass a s3 object url, and this method will extract
   * - bucket name
   * - key
   * - region
   *
   * I took this from https://stackoverflow.com/a/48543994/1724285 and fixed one bug
   *
   * @param {string} url s3 url
   */
  static parseUrl(url) {
    const decodedUrl = decodeURIComponent(url);

    let result = {
      bucket: '',
      key: '',
      region: '',
    };

    // http://s3.amazonaws.com/bucket/key1/key2
    let match = decodedUrl.match(
      /^https?:\/\/s3.amazonaws.com\/([^/]+)\/?(.*?)$/
    );
    if (match) {
      result = {
        bucket: match[1],
        key: match[2],
        region: '',
      };
    }

    // http://s3-aws-region.amazonaws.com/bucket/key1/key2
    match = decodedUrl.match(
      /^https?:\/\/s3-([^.]+).amazonaws.com\/([^/]+)\/?(.*?)$/
    );
    if (match) {
      result = {
        bucket: match[2],
        key: match[3],
        region: match[1],
      };
    }

    // http://bucket.s3.amazonaws.com/key1/key2
    match = decodedUrl.match(/^https?:\/\/([^.]+).s3.amazonaws.com\/?(.*?)$/);
    if (match) {
      result = {
        bucket: match[1],
        key: match[2],
        region: '',
      };
    }

    // http://bucket.s3-aws-region.amazonaws.com/key1/key2 or,
    // http://bucket.s3.aws-region.amazonaws.com/key1/key2
    match = decodedUrl.match(
      /^https?:\/\/([^.]+).(?:s3-|s3\.)([^.]+).amazonaws.com\/?(.*?)$/
    );
    if (match) {
      result = {
        bucket: match[1],
        key: match[3],
        region: match[2],
      };
    }

    // Replace +'s for spaces. Bucket keys are not encoded, but url encodes space as +
    // decodeURIComponent will not decode + to ' '
    result.key = result.key.replace(/\+/g, ' ');

    return result;
  }

  /** THE BELOW CODE IS DEPRECATED, ONLY KEEPING IN CASE OF EMERGENCY IE AMPLIFY FAILS */

  // Allows for creating S3 helper async style
  static async create(region = null, bucket = null) {
    const s3 = new S3Helper(region, bucket);
    await s3.configureCredentials();
    return s3;
  }

  constructor(region, bucket) {
    this.credentials = null;
    this.apiVersion = '2006-03-01';
    this.bucket = bucket;
    this.signatureVersion = 'v4';
    this.region = region;
    this.localTestingConfig = {};
    this.handler = null;
  }

  // Grab current instance and edit its configurations directly
  setBucketOptions(region, bucket) {
    const instance = this;
    instance.bucket = bucket;
    instance.region = region;
    instance.handler.config.region = region;
    this.updateEndpoints(region);
  }

  // when a region changes, the end points must be updated
  updateEndpoints(region) {
    const instance = this;
    const updatedEndpoint = `s3.${region}.amazonaws.com`;
    instance.handler.config.endpoint = updatedEndpoint;
    instance.handler.endpoint.host = updatedEndpoint;
    instance.handler.endpoint.hostname = updatedEndpoint;
    instance.handler.endpoint.href = `https://${updatedEndpoint}/`;
  }

  // Build credential obj using Amplify, customize to feed to aws-sdk
  async configureCredentials() {
    this.credentials = await Auth.currentUserCredentials();
    this.createS3Handler();
  }

  // Creates new S3 handler from aws-sdk using init values per source code
  createS3Handler() {
    const {
      apiVersion,
      signatureVersion,
      credentials,
      bucket,
      region,
      localTestingConfig,
    } = this;

    const s3Handler = new AWS.S3({
      // eslint-disable-line no-undef
      apiVersion,
      params: { Bucket: bucket },
      signatureVersion,
      region,
      credentials,
      ...localTestingConfig,
    });
    this.handler = s3Handler;
  }

  /**
   * helper function for retrieving presigned url using aws-sdk
   * @param {*} key relative file path in bucket
   * @params {object} opts optional
   */
  getPresignedUrl(key, opts) {
    if (opts) {
      const { region, bucket } = opts;
      if (!region || !bucket)
        throw new Error('Missing region or bucket in getPresignedUrl opts');
      this.setBucketOptions(region, bucket);
    }

    // NOTE: This object must be structured in this format.
    const params = {
      Bucket: this.bucket,
      Key: key,
    };
    if (this.handler) {
      const url = this.handler.getSignedUrl('getObject', params);
      return url;
    }
    return null;
  }

  async listObjects(prefix, maxKeys = 12) {
    const params = {
      Bucket: this.bucket,
      MaxKeys: maxKeys,
      Prefix: prefix,
      // Delimiter: "/",
    };
    if (this.handler) {
      const objectList = await this.handler.listObjects(params).promise();
      return objectList;
    }
    return null;
  }
}
